import modalActions from "actions/modal";
import cn from "classnames";
import { Transition } from "components/shared/column";
import DateTime from "components/shared/datetime";
import addDays from "date-fns/addDays";
import endOfToday from "date-fns/endOfToday";
import format from "date-fns/format";
import isSameDay from "date-fns/isSameDay";
import startOfToday from "date-fns/startOfToday";
import startOfYesterday from "date-fns/startOfYesterday";
import subDays from "date-fns/subDays";
import { paths } from "helpers/paths";
import { formatNumber } from "helpers/system";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Redirect, useHistory, useRouteMatch } from "react-router-dom";
import { getOdometerValues } from "sources/books/trackers";
import EditOdometerValue from "./form";

const MAX_DAYS_BACK = 3;
const dateTimeFormat = "yyyy-MM-dd'T'HH:mm:ssxxx";

const TrackerOdometerValues = ({ roles, modal }) => {
    const shouldRedirect = !roles.isTruckingOrganizationAdmin;
    const history = useHistory();
    const match = useRouteMatch();
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        from: format(startOfYesterday(), dateTimeFormat),
        to: format(endOfToday(), dateTimeFormat),
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (shouldRedirect) {
            return;
        }
        refreshData();
    }, [shouldRedirect]);

    if (shouldRedirect) {
        return <Redirect to={paths.trackers} />;
    }

    const refreshData = () => {
        if (!filters.from || !filters.to) {
            return;
        }
        setLoading(true);
        getOdometerValues({
            trackerId: parseInt(match.params.trackerId),
            from: new Date(filters.from).toISOString(),
            to: new Date(filters.to).toISOString(),
        }).then((res) => {
            setData(res || []);
            setLoading(false);
        });
    };

    const close = () => {
        history.push(paths.trackersShow(match.params.trackerId));
    };

    const getMinTime = (key) => {
        const value = filters[key];
        const compareDay = key === "to" && filters.from ? new Date(filters.from) : subDays(new Date(), MAX_DAYS_BACK);
        if (isSameDay(new Date(value), compareDay)) {
            return key === "to" && filters.from ? new Date(filters.from) : new Date();
        }
        return startOfToday();
    };

    const editRow = (row) => {
        modal({
            name: "default",
            title: "Подтверждение значения",
            windowMod: "sm",
            CustomBody: EditOdometerValue,
            bodyData: { data: row, onSave: refreshData },
        });
    };

    return (
        <div className="page-content">
            <Transition dir="full">
                <div className="page-book__list book-list list book-list--nofilter">
                    <div className="book-list__head-wrap">
                        <div className="book-list__head">
                            <div className="book-list__head-close close close--sm" onClick={close} />
                            <div className="book-list__head-title">История показаний одометра</div>
                        </div>
                    </div>
                    <div className="container-fluid my-2 d-flex align-items-start">
                        <div className="d-inline-block">
                            <DateTime
                                {...{
                                    inPortal: true,
                                    inputProps: {
                                        name: "from",
                                        type: "datetime",
                                    },
                                    libOptions: {
                                        placeholderText: "Дата начала",
                                        isClearable: true,
                                        minDate: subDays(new Date(), MAX_DAYS_BACK),
                                        maxDate: addDays(new Date(), 1),
                                        minTime: getMinTime("from"),
                                        maxTime: endOfToday(),
                                    },
                                }}
                                onChange={({ name, value }) => setFilters((f) => ({ ...f, [name]: value }))}
                                value={filters.from}
                            />
                        </div>
                        <div className="d-inline-block px-3">&mdash;</div>
                        <div className="d-inline-block">
                            <DateTime
                                {...{
                                    inPortal: true,
                                    inputProps: {
                                        name: "to",
                                        type: "datetime",
                                    },
                                    libOptions: {
                                        placeholderText: "Дата окончания",
                                        isClearable: true,
                                        minDate: filters.from
                                            ? new Date(filters.from)
                                            : subDays(new Date(), MAX_DAYS_BACK),
                                        maxDate: addDays(new Date(), 1),
                                        minTime: getMinTime("to"),
                                        maxTime: endOfToday(),
                                    },
                                }}
                                onChange={({ name, value }) => setFilters((f) => ({ ...f, [name]: value }))}
                                value={filters.to}
                            />
                        </div>
                        <div className="d-inline-block ml-2">
                            <div
                                className={cn("btn btn-sm btn-brand", { disabled: !filters.from || !filters.to })}
                                onClick={refreshData}
                                disabled={!filters.from || !filters.to}
                            >
                                Обновить
                            </div>
                        </div>
                    </div>
                    <div className="list__body-wrap">
                        <div className={cn("list__body pb-3", { loading })}>
                            <div className="overflow-auto h-100">
                                <div className="h-100 container-fluid table-responsive">
                                    <table className="table table-sm table-bordered">
                                        <thead className="bg-white sticky-header">
                                            <tr>
                                                <th>#</th>
                                                <th>Время фиксации</th>
                                                <th>Значение</th>
                                                <th>Корректное значение</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={4} className="text-center">
                                                        Загрузка...
                                                    </td>
                                                </tr>
                                            ) : data.length === 0 ? (
                                                <tr>
                                                    <td colSpan={4} className="text-center">
                                                        Нет данных
                                                    </td>
                                                </tr>
                                            ) : (
                                                data.map((row, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{new Date(row.fixedAt).toLocaleString("ru-RU")}</td>
                                                        <td className="text-right">{formatNumber(row.value)}</td>
                                                        <td>
                                                            {row.isCorrect ? (
                                                                <span className="badge badge-success">Да</span>
                                                            ) : (
                                                                <>
                                                                    <span className="badge badge-danger">Нет</span>
                                                                    <small
                                                                        className="ml-2 link"
                                                                        onClick={() => editRow(row)}
                                                                    >
                                                                        Изменить
                                                                    </small>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    modal: (params) => dispatch(modalActions.open(params)),
});

const mapStateToProps = (state) => ({
    roles: state.profile.roles,
});

export default connect(mapStateToProps, mapDispatchToProps)(TrackerOdometerValues);
